import { alpha, Theme } from '@mui/material'
import { Components } from '@mui/material/styles/components'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'

export const C1ButtonBaseTheme: Components<Theme>['MuiButtonBase'] = {
    defaultProps: {
        disableRipple: true,
        color: 'primary',
    },
}

export const C1ButtonTheme: Components<Theme>['MuiButton'] = {
    defaultProps: {
        disableElevation: true,
        disableFocusRipple: true,
        size: 'small',
    },
    styleOverrides: {
        root: ({ ownerState, theme }) => theme.unstable_sx({
            textTransform: 'none',
            borderRadius: C1BorderRadius.medium,

            ...(ownerState.size === 'small' && {
                fontSize: '14px',
                maxHeight: '30px',
                paddingY: '0.5rem',
                paddingX: 2,
                lineHeight: '1',
            }),

            '&:focus-visible': {
                outlineOffset: '1px',
                outlineWidth: '2px',
                outlineStyle: 'solid',
                outlineColor: ownerState.color === 'inherit'
                    ? 'inherit' : theme.palette[ownerState.color || 'info'].main,
            },

            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                '& .MuiSvgIcon-root': {
                    // we want icons to not cause the button to grow in height when added,
                    // but still be the right size
                    height: '0.7em',
                    width: '0.7em',
                    fontSize: '20px',
                },
            },

            // Certain components (<DenyIcon />) create an icon wrapper (and the margin that
            // comes with it) but only create the icon itself conditionally.
            // So - hide those wrappers if they don't actually contain icons.
            '& .MuiButton-startIcon:empty, & .MuiButton-endIcon:empty': {
                display: 'none',
            },

            // Default MUI margin on startIcon and endIcon is a bit much
            '& .MuiButton-startIcon': {
                marginRight: 1,
            },

            '& .MuiButton-endIcon': {
                marginLeft: 1,
            },
        }),
        text: ({ ownerState, theme }) => {
            let hoverColor
            switch (ownerState.color) {
                case 'inherit':
                    hoverColor = 'inherit'
                    break
                case 'primary':
                    hoverColor = theme.palette.primary.dark
                    break
                case 'error':
                    hoverColor = theme.palette.error.dark
                    break
                case 'success':
                    hoverColor = theme.palette.success.main
                    break
                default:
                    hoverColor = theme.palette[ownerState.color || 'info'].dark
            }

            return theme.unstable_sx({
                '&:hover': {
                    color: hoverColor,
                },

                '&:disabled': {
                    backgroundColor: theme.palette.grey[200],
                },
            })
        },
        outlined: ({ ownerState, theme }) => {
            let defaultColor
            let borderColor
            let hoverColor
            let hoverBorderColor
            let hoverBackgroundColor = 'background.default'

            switch (ownerState.color) {
                case 'inherit':
                    defaultColor = 'inherit'
                    hoverColor = 'inherit'
                    hoverBorderColor = 'inherit'
                    borderColor = 'inherit'
                    break
                case 'success':
                    defaultColor = theme.palette.success.main
                    hoverBackgroundColor = theme.palette.success.extraLight
                    borderColor = alpha(theme.palette.success.main, 0.5)
                    hoverBorderColor = borderColor
                    break
                case 'error':
                    defaultColor = theme.palette.error.main
                    hoverBackgroundColor = theme.palette.error.extraLight
                    borderColor = alpha(theme.palette.error.main, 0.5)
                    hoverColor = theme.palette.error.dark
                    hoverBorderColor = borderColor
                    break
                case 'primary':
                    borderColor = theme.palette.border.primary
                    defaultColor = theme.palette.primary.dark
                    hoverBorderColor = theme.palette.primary.main
                    hoverBackgroundColor = theme.palette.primary.extraLight
                    break
                case 'info':
                    defaultColor = theme.palette.info.main
                    borderColor = theme.palette.border.primary
                    hoverBorderColor = '#000000'
                    break
                default:
                    defaultColor = theme.palette[ownerState.color || 'info'].main
                    hoverColor = theme.palette[ownerState.color || 'info'].dark
                    borderColor = theme.palette.border.primary
                    hoverBorderColor = borderColor
            }

            return theme.unstable_sx({
                // Make outlined buttons exact same width as contained buttons
                paddingLeft: `calc(${theme.spacing(2)} - 1px)`,
                paddingRight: `calc(${theme.spacing(2)} - 1px)`,
                paddingTop: 'calc(0.5rem - 1px)',
                paddingBottom: 'calc(0.5rem - 1px)',
                backgroundColor: 'background.default',
                color: defaultColor,
                borderColor,
                '&:hover': {
                    color: hoverColor,
                    borderColor: hoverBorderColor === 'inherit'
                        ? hoverBorderColor
                        : (hoverBorderColor ? alpha(hoverBorderColor, 0.5) : 'unset'),
                    backgroundColor: hoverBackgroundColor,
                },

                '&:disabled': {
                    borderColor: theme.palette.border.primary,
                    color: theme.palette.grey[400],
                },
            })
        },
    },
}
